// src/components/users/UserDialog.tsx
import React, { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select'
import type { Branch } from '@/types/branch'
import { Sparkles, KeyRound, Mail, UserRound, Building2 } from 'lucide-react'

const userSchema = z.object({
  name: z.string().min(2),
  email: z.string().email(),
  password: z
    .string()
    .optional()
    .refine(val => !val || val.trim().length >= 6, {
      message: 'Password must be at least 6 characters',
    }),
  role: z.enum(['admin', 'branch']),
  branch_id: z.string().optional(),
})

type FormData = z.infer<typeof userSchema>

interface UserDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (data: FormData) => void
  defaultValues?: Partial<FormData>
  mode: 'add' | 'edit'
  branches: Branch[]
}

export default function UserDialog({
  open,
  onOpenChange,
  onSubmit,
  defaultValues,
  mode,
  branches,
}: UserDialogProps) {
  const [showPasswordFields, setShowPasswordFields] = useState(mode === 'add')

  const form = useForm<FormData>({
    resolver: zodResolver(userSchema),
    defaultValues:
      defaultValues || {
        name: '',
        email: '',
        password: '',
        role: 'branch',
        branch_id: '',
      },
  })

  useEffect(() => {
    const normalizedDefaults = {
      name: defaultValues?.name ?? '',
      email: defaultValues?.email ?? '',
      password: '',
      role: defaultValues?.role ?? 'branch',
      branch_id: defaultValues?.branch_id ?? '',
    }
    form.reset(normalizedDefaults)
    setShowPasswordFields(mode === 'add')
  }, [defaultValues, form, mode])

  const role = form.watch('role')

  const handle = (data: FormData) => {
    if (mode === 'add' && !data.password) {
      form.setError('password', {
        type: 'manual',
        message: 'Password is required for new users',
      })
      return
    }

    const payload: FormData = {
      ...data,
      password: (showPasswordFields || mode === 'add') && data.password
        ? data.password.trim()
        : undefined,
      branch_id: data.branch_id || undefined,
    }

    onSubmit(payload)
  }

  const showBranchSelect = role === 'branch'
  const branchOptions = useMemo(() => branches ?? [], [branches])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg border border-white/10 bg-gradient-to-br from-slate-950 to-slate-900 text-white shadow-2xl">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-2xl bg-gradient-to-r from-purple-500 to-indigo-500 flex items-center justify-center shadow-lg">
              <Sparkles className="h-5 w-5" />
            </div>
            <div>
              <DialogTitle className="text-2xl font-semibold text-white">
                {mode === 'add' ? 'Add User' : 'Edit User'}
              </DialogTitle>
              <p className="text-sm text-white/60">
                {mode === 'add'
                  ? 'Create a new teammate with secure defaults.'
                  : 'Update profile details or refresh credentials.'}
              </p>
            </div>
          </div>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handle)}
            className="space-y-6 pt-4"
          >
            <section className="space-y-4 rounded-2xl border border-white/10 bg-white/[0.03] p-4">
              <div className="flex items-center justify-between text-white/70">
                <span className="text-sm font-semibold tracking-wide uppercase">Account Details</span>
                <Badge variant="outline" className="border-white/20 text-white/70">
                  <UserRound className="mr-1 h-3.5 w-3.5" /> Profile
                </Badge>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-white/70">Full Name</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <UserRound className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                          <Input
                            {...field}
                            className="pl-9 border-white/10 bg-white/5 text-white placeholder:text-white/50"
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-white/70">Email</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                          <Input
                            {...field}
                            type="email"
                            className="pl-9 border-white/10 bg-white/5 text-white placeholder:text-white/50"
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="role"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-white/70">Role</FormLabel>
                      <FormControl>
                        <Select
                          value={field.value}
                          onValueChange={field.onChange}
                        >
                          <SelectTrigger className="border-white/10 bg-white/5 text-white">
                            <SelectValue placeholder="Select role" />
                          </SelectTrigger>
                          <SelectContent className="bg-slate-900 text-white">
                            <SelectItem value="admin">Admin</SelectItem>
                            <SelectItem value="branch">Branch</SelectItem>
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {showBranchSelect && (
                  <FormField
                    control={form.control}
                    name="branch_id"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-white/70">Branch</FormLabel>
                        <FormControl>
                          <Select
                            value={field.value || ''}
                            onValueChange={field.onChange}
                          >
                            <SelectTrigger className="border-white/10 bg-white/5 text-white">
                              <SelectValue placeholder="Select branch" />
                            </SelectTrigger>
                            <SelectContent className="bg-slate-900 text-white">
                              {branchOptions.map((b) => (
                                <SelectItem key={b.id} value={b.id}>
                                  <div className="flex items-center gap-2">
                                    <Building2 className="h-4 w-4" />
                                    {b.name}
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </div>
            </section>

            <section className="space-y-4 rounded-2xl border border-white/10 bg-white/[0.03] p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold uppercase tracking-wide text-white/70">Security</p>
                  <p className="text-xs text-white/50">Update credentials when access needs to change.</p>
                </div>
                <Badge variant="outline" className="border-white/20 text-white/70">
                  <KeyRound className="mr-1 h-3.5 w-3.5" /> Password
                </Badge>
              </div>

              {mode === 'edit' && (
                <div className="flex items-center justify-between rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3">
                  <div>
                    <p className="text-sm font-medium text-white">Update password</p>
                    <p className="text-xs text-white/50">Toggle to enable password reset for this user.</p>
                  </div>
                  <Switch
                    checked={showPasswordFields}
                    onCheckedChange={() => {
                      setShowPasswordFields((prev) => !prev)
                      form.setValue('password', '')
                    }}
                  />
                </div>
              )}

              {(showPasswordFields || mode === 'add') && (
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-white/70">
                        {mode === 'add' ? 'Temporary Password' : 'New Password'}
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="password"
                          placeholder="Min. 6 characters"
                          className="border-white/10 bg-white/5 text-white placeholder:text-white/50"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
            </section>

            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-purple-500 to-indigo-500 text-white shadow-lg hover:shadow-purple-500/30"
            >
              {mode === 'add' ? 'Add User' : 'Save Changes'}
            </Button>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
